
import AdditionalOptionsPathSegmentView from '@/components/AdditionalOptionsPathSegmentView.vue';
import HeadlinePathSegmentView from '@/components/HeadlinePathSegmentView.vue';
import PackagePathSegmentView from '@/components/PackagePathSegmentView.vue';
import SimplePathSegmentView from '@/components/SimplePathSegmentView.vue';
import { HeadlinePathSegment } from '@/models/headline-path-segment';
import { PathSegment } from '@/models/path-segment';
import { Actions } from '@/store/actions';
import { computed, ComputedRef, defineComponent, PropType } from 'vue';
import { useStore } from 'vuex';

export default defineComponent({
  name: 'PathView',
  components: {
    HeadlinePathSegmentView,
    AdditionalOptionsPathSegmentView,
    PackagePathSegmentView,
    SimplePathSegmentView
  },
  props: {
    displayedSegments: {
      type: [] as PropType<PathSegment[]>,
      required: false,
      default: null
    }
  },
  setup(props) {
    const store = useStore();

    function dataObjectComparator(
        a: PathSegment,
        b: PathSegment) {
      if (!a.dataObject || !b.dataObject) {
        return 0;
      }

      if (a.dataObject?.sorting > b.dataObject?.sorting) {
        return 1;
      }

      if (a.dataObject?.sorting < b.dataObject?.sorting) {
        return -1;
      }

      return 0;
    }

    function getTypeClass(segment: PathSegment): string {
      const typeLower = segment.type.toLowerCase();
      return typeLower.replace('pathsegment', '');
    }

    function isSimpleSegment(segment: PathSegment): boolean {
      if(!segment) {
        return false;
      }
      return segment.type === 'SimplePathSegment';
    }

    function isPackageSegment(segment: PathSegment): boolean {
      if(!segment) {
        return false;
      }
      return segment.type === 'PackagePathSegment';
    }

    function isAdditionalOptionsSegment(segment: PathSegment): boolean {
      if(!segment) {
        return false;
      }
      return segment.type === 'AdditionalOptionsPathSegment';
    }

    function isHeadlineSegment(segment: PathSegment): boolean {
      if(!segment) {
        return false;
      }
      return segment.type === 'HeadlinePathSegment';
    }

    function submitSelection(event: Event): boolean {
      event.preventDefault();
      store.dispatch(
          Actions.SubmitPathSegmentSelection,
          props.displayedSegments?.find(segment => segment.chosen) ?? props.displayedSegments?.find(
              segment => isAdditionalOptionsSegment(segment))
      );
      return false;
    }

    /**
     * returns the displayed segments, excluding headlines, sorted by the sorting key.
     */
    const displayedSegmentsNoHeadlines: ComputedRef<PathSegment[]> = computed(() => {
      return props.displayedSegments?.filter((segment: PathSegment) => !isHeadlineSegment(segment))
                  .sort(dataObjectComparator) ?? [];
    });

    /**
     * returns the displayed Headlines sorted by the sort key.
     */
    const displayedHeadlineSegments: ComputedRef<HeadlinePathSegment[]> = computed(() => {
      if (!props.displayedSegments) {
        return [];
      }
      return props.displayedSegments
                  .filter((segment: PathSegment) => isHeadlineSegment(segment))
                  .map(segment => segment as HeadlinePathSegment)
                  .sort(dataObjectComparator);
    });

    const isMobile: ComputedRef<boolean> = computed(() => {
      return store.state.isMobile;
    });

    /**
     * Returns the number of chosen columns for larger desktops
     */
    const columns: ComputedRef<string> = computed(() => {
      return store.state.morticianSettings?.columns ?? '';
    });

    const columnsNoGutter: ComputedRef<boolean> = computed(() => {
      return store.state.morticianSettings?.columnsNoGutter ?? '';
    });

    return {
      getTypeClass,
      isSimpleSegment,
      isPackageSegment,
      isAdditionalOptionsSegment,
      submitSelection,
      displayedSegmentsNoHeadlines,
      displayedHeadlineSegments,
      isMobile,
      columns,
      columnsNoGutter
    };
  }
});
